import { useSyncExternalStore } from 'react';

/**
 * ~25 lines instead of a state library. The app has one wizard and one dev
 * panel; a dependency would be more surface than the problem.
 *
 * State is replaced wholesale on every set, so the snapshot identity is
 * stable between notifications and useSyncExternalStore never loops.
 */
export type Store<T> = {
  get(): T;
  set(patch: Partial<T> | ((s: T) => Partial<T>)): void;
  subscribe(fn: () => void): () => void;
  use(): T;
};

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const subs = new Set<() => void>();

  const get = () => state;
  const subscribe = (fn: () => void) => {
    subs.add(fn);
    return () => {
      subs.delete(fn);
    };
  };

  return {
    get,
    subscribe,
    set(patch) {
      const next = typeof patch === 'function' ? patch(state) : patch;
      state = { ...state, ...next };
      for (const fn of [...subs]) fn();
    },
    use() {
      return useSyncExternalStore(subscribe, get, get);
    },
  };
}
